import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';

const API = 'https://api.ctexpresskh.com/api';
const token = () => localStorage.getItem('token');
const statusLabel = { pending:'Pending', cn_wh:'CN WH', in_transit:'In Transit', kh_wh:'KH WH', delivery:'Delivery', signed:'Signed' };
const statusColor = { pending:'#f59e0b', cn_wh:'#3b82f6', in_transit:'#8b5cf6', kh_wh:'#06b6d4', delivery:'#f97316', signed:'#10b981' };
const ALL_STATUSES = ['pending','cn_wh','in_transit','kh_wh','delivery','signed'];

export default function Reports() {
  const [shipments, setShipments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(API + '/shipments?limit=1000', { headers: { Authorization: 'Bearer ' + token() } });
      const data = await res.json();
      setShipments(data.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let result = [...shipments];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        (s.tracking_number||'').toLowerCase().includes(q) ||
        (s.receiver_name||'').toLowerCase().includes(q) ||
        (s.receiver_phone||'').toLowerCase().includes(q) ||
        (s.receiver_customer_code||'').toLowerCase().includes(q) ||
        (s.goods_description||'').toLowerCase().includes(q)
      );
    }
    if (status) result = result.filter(s => s.status === status);
    if (method) result = result.filter(s => s.shipping_method === method);
    if (dateFrom) result = result.filter(s => new Date(s.updated_at||s.created_at) >= new Date(dateFrom));
    if (dateTo) result = result.filter(s => new Date(s.updated_at||s.created_at) <= new Date(dateTo + 'T23:59:59'));
    setFiltered(result);
  }, [shipments, search, status, method, dateFrom, dateTo]);

  const totalFee = filtered.reduce((sum, s) => sum + parseFloat(s.shipping_fee || 0), 0);
  const totalQty = filtered.reduce((sum, s) => sum + (parseInt(s.quantity) || 1), 0);
  const totalWeight = filtered.reduce((sum, s) => sum + parseFloat(s.weight_kg || 0), 0);
  const totalCbm = filtered.reduce((sum, s) => sum + (parseFloat(s.cbm || 0) * (parseInt(s.quantity) || 1)), 0);
  const collectedFee = filtered.filter(s => s.status === 'signed').reduce((sum, s) => sum + parseFloat(s.shipping_fee || 0), 0);

  const clearFilters = () => { setSearch(''); setStatus(''); setMethod(''); setDateFrom(''); setDateTo(''); };

  const printReport = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>CT Express Report</title>
    <style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px;}h1{font-size:18px;color:#f97316;}
    .meta{color:#64748b;margin-bottom:16px;font-size:11px;}
    .summary{display:flex;gap:20px;margin-bottom:16px;}
    .sum-box{border:1px solid #e2e8f0;border-radius:6px;padding:10px 16px;}
    .sum-val{font-size:18px;font-weight:bold;color:#f97316;}.sum-lbl{font-size:10px;color:#64748b;}
    table{width:100%;border-collapse:collapse;}
    th{background:#f8fafc;padding:8px;text-align:left;border-bottom:2px solid #e2e8f0;font-size:11px;color:#64748b;}
    td{padding:7px 8px;border-bottom:1px solid #f1f5f9;font-size:11px;}
    .trk{font-weight:bold;color:#f97316;}@media print{button{display:none;}}</style>
    </head><body>
    <h1>CT Express Report</h1>
    <div class="meta">Generated: ${new Date().toLocaleString('en-GB')} | Orders: ${filtered.length} | Total Qty: ${totalQty}</div>
    <div class="summary">
      <div class="sum-box"><div class="sum-val">${totalQty}</div><div class="sum-lbl">Total Qty (${filtered.length} orders)</div></div>
      <div class="sum-box"><div class="sum-val">$${totalFee.toFixed(2)}</div><div class="sum-lbl">Total Revenue</div></div>
      <div class="sum-box"><div class="sum-val">$${collectedFee.toFixed(2)}</div><div class="sum-lbl">Collected</div></div>
    </div>
    <table><thead><tr><th>#</th><th>Tracking</th><th>User ID</th><th>Receiver</th><th>Phone</th><th>Description</th><th>Method</th><th>ចំនួន/Qty</th><th>Weight</th><th>CBM(m³)</th><th>Fee</th><th>Status</th><th>Date</th></tr></thead>
    <tbody>${filtered.map((s,i) => `<tr><td>${i+1}</td><td class=trk>${s.tracking_number}</td><td>${s.receiver_customer_code||''}</td><td>${s.receiver_name||''}</td><td>${s.receiver_phone||''}</td><td>${s.goods_description||''}</td><td>${(s.shipping_method||'').toUpperCase()}</td><td>${s.quantity||1}</td><td>${s.weight_kg||0}kg</td><td>${parseFloat(s.cbm||0).toFixed(4)}</td><td>$${parseFloat(s.shipping_fee||0).toFixed(2)}</td><td>${statusLabel[s.status]||s.status}</td><td>${new Date(s.updated_at||s.created_at).toLocaleDateString('en-GB')}</td></tr>`).join('')}
    </tbody></table>
    <script>window.print();<\/script></body></html>`);
    win.document.close();
  };

  const exportCSV = () => {
    const headers = ['#','Tracking','User ID','Receiver','Phone','Description','Method','Type','ចំនួន/Qty','Weight(kg)','CBM(m3)','Fee','Status','Date'];
    const rows = filtered.map((s,i) => [i+1,s.tracking_number,s.receiver_customer_code||'',s.receiver_name,s.receiver_phone,s.goods_description||'',s.shipping_method,s.shipping_type,s.quantity||1,s.weight_kg,s.cbm||'',s.shipping_fee,s.status,new Date(s.updated_at||s.created_at).toLocaleDateString('en-GB')]);
    const csv = [headers,...rows].map(r => r.map(v => '"'+(v||'')+'"').join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'ct-express-report-'+new Date().toISOString().slice(0,10)+'.csv'; a.click();
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:24, color:'#1e293b' }}>Reports</h1>
          <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:13 }}>Filter, print and export shipment data</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={printReport} style={{ padding:'9px 20px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>Print</button>
          <button onClick={exportCSV} style={{ padding:'9px 20px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>Export CSV</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        {[
          { label:'Total Shipments', value:totalQty + ' pcs (' + filtered.length + ' orders)', color:'#3b82f6' },
          { label:'Total Revenue', value:'$'+totalFee.toFixed(2), color:'#f97316' },
          { label:'Collected', value:'$'+collectedFee.toFixed(2), color:'#10b981' },
          { label:'Pending Revenue', value:'$'+(totalFee-collectedFee).toFixed(2), color:'#f59e0b' },
        ].map((c,i) => (
          <div key={i} style={{ background:'#fff', borderRadius:10, padding:16, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', borderLeft:'4px solid '+c.color }}>
            <div style={{ fontSize:22, fontWeight:800, color:c.color }}>{c.value}</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters - same as Shipments page */}
      <div style={{ background:'#fff', borderRadius:12, padding:14, marginBottom:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, minWidth:180, border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 12px' }}>
            <Search size={14} color="#94a3b8" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tracking, name, phone, ID..."
              style={{ border:'none', outline:'none', flex:1, fontSize:13 }} />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }}>
            <option value="">All Status</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{statusLabel[s]}</option>)}
          </select>
          <select value={method} onChange={e => setMethod(e.target.value)} style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }}>
            <option value="">All Methods</option>
            <option value="sea">Sea</option><option value="land">Land</option><option value="air">Air</option>
          </select>
          <span style={{ fontSize:12, color:'#64748b' }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }} />
          <span style={{ fontSize:12, color:'#64748b' }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }} />
          <button onClick={clearFilters} style={{ padding:'7px 12px', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', fontSize:13 }}>Clear</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead style={{ background:'#f8fafc' }}>
            <tr>
              {['#','Tracking','User ID','Receiver','Phone','Description','Method','Type','ចំនួន/Qty','Weight','CBM','Fee','Status','Date'].map(h => (
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:'#64748b', fontWeight:600, borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={13} style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>No shipments found</td></tr>
            ) : filtered.map((s,i) => (
              <tr key={s.id} style={{ borderBottom:'1px solid #f8fafc' }}
                onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <td style={{ padding:'8px 12px', color:'#94a3b8', fontSize:11 }}>{i+1}</td>
                <td style={{ padding:'8px 12px', fontWeight:700, color:'#f97316', fontSize:12, whiteSpace:'nowrap' }}>{s.tracking_number}</td>
                <td style={{ padding:'8px 12px', fontSize:11 }}>
                  <div style={{ fontWeight:700, color:'#f97316', fontSize:11 }}>{s.receiver_customer_code||'-'}</div>
                </td>
                <td style={{ padding:'8px 12px', fontWeight:500 }}>{s.receiver_name||'-'}</td>
                <td style={{ padding:'8px 12px', color:'#64748b', fontSize:12 }}>{s.receiver_phone}</td>
                <td style={{ padding:'8px 12px', color:'#64748b', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.goods_description||'-'}</td>
                <td style={{ padding:'8px 12px', fontWeight:600, textTransform:'uppercase', fontSize:11 }}>{s.shipping_method}</td>
                <td style={{ padding:'8px 12px' }}>
                  <span style={{ background:s.shipping_type==='cbm'?'#dbeafe':'#fef3c7', color:s.shipping_type==='cbm'?'#1d4ed8':'#92400e', padding:'2px 6px', borderRadius:8, fontSize:10, fontWeight:700 }}>
                    {(s.shipping_type||'').toUpperCase()}
                  </span>
                </td>
                <td style={{ padding:'8px 12px', textAlign:'center', fontWeight:700 }}>{s.quantity||1}</td>
                <td style={{ padding:'8px 12px', color:'#64748b', whiteSpace:'nowrap' }}>{s.weight_kg||'-'}kg</td>
                <td style={{ padding:'8px 12px', color:'#64748b', whiteSpace:'nowrap' }}>{s.cbm ? (parseFloat(s.cbm)*(s.quantity||1)).toFixed(4) : '-'}</td>
                <td style={{ padding:'8px 12px', fontWeight:700 }}>${parseFloat(s.shipping_fee||0).toFixed(2)}</td>
                <td style={{ padding:'8px 12px' }}>
                  <span style={{ background:(statusColor[s.status]||'#gray')+'20', color:statusColor[s.status], padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>
                    {statusLabel[s.status]||s.status}
                  </span>
                </td>
                <td style={{ padding:'8px 12px', color:'#94a3b8', fontSize:11, whiteSpace:'nowrap' }}>{new Date(s.updated_at||s.created_at).toLocaleDateString('en-GB', {timeZone:'Asia/Phnom_Penh'})}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot style={{ background:'#fafafa', borderTop:'2px solid #e2e8f0' }}>
              <tr>
                <td colSpan={8} style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b' }}>Total ({filtered.length} records)</td>
                <td style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b', textAlign:'center' }}>{totalQty}</td>
                <td style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b' }}>{totalWeight.toFixed(3)}kg</td>
                <td style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b' }}>{totalCbm.toFixed(4)}</td>
                <td style={{ padding:'10px 12px', fontWeight:800, color:'#f97316', fontSize:15 }}>${totalFee.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div style={{ textAlign:'center', marginTop:20, fontSize:11, color:'#94a3b8' }}>
        Developed by Ven Sophanith
      </div>
    </div>
  );
}
